import { TeamTagSchema } from '@rhythmic/types'
import type { InsertMutationFnParams, UpdateMutationFnParams } from '@tanstack/vue-db'
import {
  createMutationResult,
  normaliseChanges,
  resolveApiEndpoint,
} from '../CollectionRegistry/CollectionBuilder.helpers'
import type { CollectionBuildRequestConfig } from '../CollectionRegistry/CollectionBuilder.types'

export const TEAM_TAG_TABLE = '"public"."teamTags"'

const JSON_HEADERS = {
  'Content-Type': 'application/json',
  accept: 'application/json',
} as const

function stripTagTimestamps(payload: Record<string, unknown>) {
  const { created_at, updated_at, ...rest } = payload
  return rest
}

export const TeamTagConfig: CollectionBuildRequestConfig = {
  collectionType: 'electric',
  table: TEAM_TAG_TABLE,
  partitionKey: 'team_id',
  key: 'id',
  apiEndpoint: '/api/team/[team_id]/tags',
  where: {},
  schema: TeamTagSchema,
  mutators: {
    insert: async (params: InsertMutationFnParams) => {
      const txids = await Promise.all(
        params.transaction.mutations.map(async (mutation) => {
          const record = mutation.modified as Record<string, unknown> | undefined
          if (!record) return undefined
          const endpoint = resolveApiEndpoint(TeamTagConfig, { record })
          const body = normaliseChanges(stripTagTimestamps(record))
          const response = (await $fetch(endpoint, {
            method: 'POST',
            headers: JSON_HEADERS,
            credentials: 'include',
            body: JSON.stringify(body),
          })) as any
          const txidCandidate = Number.parseInt(response?.txid)
          return Number.isNaN(txidCandidate) ? undefined : txidCandidate
        }),
      )
      return createMutationResult(txids.filter((value): value is number => typeof value === 'number'))
    },
    update: async (params: UpdateMutationFnParams) => {
      const txids = await Promise.all(
        params.transaction.mutations.map(async (mutation) => {
          const candidateId = mutation.modified?.[TeamTagConfig.key] as string | number | undefined
          if (candidateId === undefined || candidateId === null) return undefined
          const recordContext = (mutation.modified ?? mutation.original ?? TeamTagConfig.where) as
            | Record<string, unknown>
            | undefined
          const endpoint = resolveApiEndpoint(TeamTagConfig, {
            record: recordContext,
            resourceId: candidateId,
          })
          const changes = normaliseChanges(stripTagTimestamps(mutation.changes))
          if (Object.keys(changes).length === 0) return undefined
          const response = (await $fetch(endpoint, {
            method: 'PUT',
            headers: JSON_HEADERS,
            credentials: 'include',
            body: JSON.stringify(changes),
          })) as any
          const txidCandidate = Number.parseInt(response?.txid)
          return Number.isNaN(txidCandidate) ? undefined : txidCandidate
        }),
      )
      return createMutationResult(txids.filter((value): value is number => typeof value === 'number'))
    },
  },
}
